import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import SettingMenu from './settingMenu';
interface BillingContact {
  id: string;
  name: string;
  email: string;
  phone?: string;
}

interface PrimaryContact {
  name: string;
  email: string;
}

interface CompanyDetails {
  accountName: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
  timezone: string;
}

const AccountContactsPage: React.FC = () => {
  const [billingContacts, setBillingContacts] = useState<BillingContact[]>([
    { id: '1', name: '', email: '', phone: '' }
  ]);

  const {
    register: registerCompany,
    handleSubmit: handleSubmitCompany,
    formState: { errors: companyErrors }
  } = useForm<CompanyDetails>();

  const {
    register: registerPrimary,
    handleSubmit: handleSubmitPrimary,
    formState: { errors: primaryErrors }
  } = useForm<PrimaryContact>();

  const handleAddBillingContact = () => {
    const newContact: BillingContact = {
      id: Date.now().toString(),
      name: '',
      email: '',
      phone: ''
    };
    setBillingContacts([...billingContacts, newContact]);
  };

  const handleRemoveBillingContact = (id: string) => {
    if (billingContacts.length > 1) {
      setBillingContacts(billingContacts.filter((contact) => contact.id !== id));
    }
  };

  const onSubmitCompany = (data: CompanyDetails) => {
    console.log('Company details:', data);
  };

  const onSubmitPrimary = (data: PrimaryContact) => {
    console.log('Primary contact:', data);
  };

  const handleSaveAll = () => {
    console.log('Save all changes');
  };

  return (
    <>
      <SettingMenu />
      <div>
        <div>
          <h1 className="text-2xl font-semibold text-gray-800 mb-6">
            Account Contacts & Company Details
          </h1>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-md shadow-lg p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-6">Company Information</h2>

              <form onSubmit={handleSubmitCompany(onSubmitCompany)} className="space-y-4">
                <div>
                  <label className="block mb-1 font-medium text-gray-700">
                    Account name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Webcastcloud Pty"
                    {...registerCompany('accountName', { required: 'Account name is required' })}
                  />
                  {companyErrors.accountName && (
                    <p className="text-red-500 text-sm mt-1">{companyErrors.accountName.message}</p>
                  )}
                </div>

                <div>
                  <label className="block mb-1 font-medium text-gray-700">
                    Business address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Address line 1"
                    {...registerCompany('addressLine1', { required: 'Address is required' })}
                  />
                  {companyErrors.addressLine1 && (
                    <p className="text-red-500 text-sm mt-1">
                      {companyErrors.addressLine1.message}
                    </p>
                  )}
                </div>

                <div>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Address line 2"
                    {...registerCompany('addressLine2')}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="City"
                      {...registerCompany('city', { required: 'City is required' })}
                    />
                    {companyErrors.city && (
                      <p className="text-red-500 text-sm mt-1">{companyErrors.city.message}</p>
                    )}
                  </div>
                  <div>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      {...registerCompany('state', { required: 'State is required' })}>
                      <option value="">Select State</option>
                      <option value="NSW">New South Wales</option>
                      <option value="VIC">Victoria</option>
                      <option value="QLD">Queensland</option>
                      <option value="WA">Western Australia</option>
                      <option value="SA">South Australia</option>
                      <option value="TAS">Tasmania</option>
                      <option value="ACT">ACT</option>
                      <option value="NT">Northern Territory</option>
                    </select>
                    {companyErrors.state && (
                      <p className="text-red-500 text-sm mt-1">{companyErrors.state.message}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Postcode"
                      {...registerCompany('postcode', { required: 'Postcode is required' })}
                    />
                    {companyErrors.postcode && (
                      <p className="text-red-500 text-sm mt-1">{companyErrors.postcode.message}</p>
                    )}
                  </div>
                  <div>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Country"
                      {...registerCompany('country', { required: 'Country is required' })}
                    />
                    {companyErrors.country && (
                      <p className="text-red-500 text-sm mt-1">{companyErrors.country.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block mb-1 font-medium text-gray-700">
                    Time zone <span className="text-red-500">*</span>
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    {...registerCompany('timezone', { required: 'Timezone is required' })}>
                    <option value="">Select Timezone</option>
                    <option value="Australia/Sydney">Australia - NSW</option>
                    <option value="Australia/Melbourne">Australia - VIC</option>
                    <option value="Australia/Brisbane">Australia - QLD</option>
                    <option value="Australia/Perth">Australia - WA</option>
                    <option value="Australia/Adelaide">Australia - SA</option>
                  </select>
                  {companyErrors.timezone && (
                    <p className="text-red-500 text-sm mt-1">{companyErrors.timezone.message}</p>
                  )}
                </div>
              </form>
            </div>

            <div className="space-y-6">
              <div className="bg-white rounded-md shadow-lg p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-6">Primary Contact</h2>

                <form onSubmit={handleSubmitPrimary(onSubmitPrimary)} className="space-y-4">
                  <div>
                    <label className="block mb-1 font-medium text-gray-700">
                      Contact Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="John Doe"
                      {...registerPrimary('name', { required: 'Contact name is required' })}
                    />
                    {primaryErrors.name && (
                      <p className="text-red-500 text-sm mt-1">{primaryErrors.name.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block mb-1 font-medium text-gray-700">
                      Contact Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="john.doe@example.com"
                      {...registerPrimary('email', {
                        required: 'Contact email is required',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Invalid email address'
                        }
                      })}
                    />
                    {primaryErrors.email && (
                      <p className="text-red-500 text-sm mt-1">{primaryErrors.email.message}</p>
                    )}
                  </div>
                </form>
              </div>

              <div className="bg-white rounded-md shadow-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-gray-800">Billing Contacts</h2>

                  <button
                    type="button"
                    onClick={handleAddBillingContact}
                    className="px-6 btn-primary w-40 mr-2 ml-0 mt-0">
                    + Add Contact
                  </button>
                </div>

                <div className="space-y-6">
                  {billingContacts.map((contact, index) => (
                    <div key={contact.id} className="border border-gray-200 rounded-md p-4">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-medium text-gray-700">Billing Contact {index + 1}</h3>
                        {billingContacts.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveBillingContact(contact.id)}
                            className="text-red-500 hover:text-red-700 text-sm font-medium">
                            Remove
                          </button>
                        )}
                      </div>

                      <div className="space-y-4">
                        <div>
                          <label className="block mb-1 font-medium text-gray-700">
                            Contact Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Jane Smith"
                            defaultValue={contact.name}
                          />
                        </div>

                        <div>
                          <label className="block mb-1 font-medium text-gray-700">
                            Contact Email <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="email"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="jane.smith@example.com"
                            defaultValue={contact.email}
                          />
                        </div>

                        <div>
                          <label className="block mb-1 font-medium text-gray-700">
                            Contact Phone <span className="text-gray-500">(optional)</span>
                          </label>
                          <input
                            type="tel"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="+61 400 000 000"
                            defaultValue={contact.phone}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 flex items-center gap-4">
            <button
              type="button"
              onClick={handleSaveAll}
              className="px-6 btn-primary w-28 mr-2 ml-0 mt-0">
              Save
            </button>

            <button
              type="button"
              className="mx-0 px-6 btn-primary discard-btn w-28 mt-0"
              data-bs-dismiss="modal">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AccountContactsPage;
